import { DICE_COUNT, MAX_ROLLS } from './types';
import { rngFromString, type Rng } from './rng';
import { DIE_PRICE, GLASS_USES, rollDiceSet, type Chaos, type DieKind } from './dice';
import {
  scoreHandCtx,
  TALISMAN_BY_ID,
  TALISMANS,
  type BossRule,
  type ScoredHandFull,
  type TalismanRarity,
} from './talismans';
import {
  HANDS_PER_ROUND,
  SHOP_REROLL_COST,
  TARGET_BASE,
  TARGET_GROWTH,
  TILT_MAX,
  TILT_PER_FORCE,
  TOTAL_ROUNDS,
} from './run-constants';

export {
  HANDS_PER_ROUND,
  SHOP_REROLL_COST,
  TARGET_BASE,
  TARGET_GROWTH,
  TILT_MAX,
  TILT_PER_FORCE,
  TOTAL_ROUNDS,
};

/**
 * La run (brief §4.1): 12 round, obiettivo crescente, max 3 mani per round.
 * Ogni 3 round un BOSS distorce una regola. Tutto deterministico da
 * (seed, personaggio, sequenza azioni): daily, replay-verify, simulatore.
 */

// ── Personaggi: regole di partenza (brief §7) ───────────────────────────────

export type CharacterId = 'bilanciato' | 'fabbro' | 'spiritista' | 'berserker';

export interface CharacterDef {
  id: CharacterId;
  slots: number;
  targetScale: number;
  roundStartTilt: number;
  startDie?: DieKind;
  dieDiscount?: number;
  riskDiscount?: number;
}

export const CHARACTERS: Record<CharacterId, CharacterDef> = {
  bilanciato: { id: 'bilanciato', slots: 5, targetScale: 1, roundStartTilt: 0 },
  fabbro: { id: 'fabbro', slots: 5, targetScale: 1, roundStartTilt: 0, startDie: 'bomba', dieDiscount: 2 },
  spiritista: { id: 'spiritista', slots: 6, targetScale: 1.15, roundStartTilt: 0 },
  berserker: { id: 'berserker', slots: 5, targetScale: 1, roundStartTilt: 50, riskDiscount: 2 },
};

// ── Boss: una regola distorta ogni 3 round ──────────────────────────────────

export const BOSS_RULES: BossRule[] = ['no-sei', 'pool-rilanci', 'minimo-tris', 'tremante'];
/** Rilanci liberi condivisi in TUTTO il round col boss "Contingente". */
export const BOSS_POOL_REROLLS = 2;

export function isBossRound(round: number): boolean {
  return round % 3 === 0 && round <= TOTAL_ROUNDS;
}

export function targetForRound(round: number, scale = 1): number {
  return Math.round((TARGET_BASE * Math.pow(TARGET_GROWTH, round - 1) * scale) / 5) * 5;
}

/** Ricompensa a fine round: base + mani risparmiate + interessi (max 3). */
export function roundReward(handsLeft: number, pips: number): number {
  return 4 + handsLeft + Math.min(3, Math.floor(pips / 5));
}

export interface ShopTalisman {
  id: string;
  price: number;
  sold: boolean;
}

export interface ShopDie {
  kind: Exclude<DieKind, 'standard'>;
  price: number;
  sold: boolean;
}

export interface ShopOffer {
  talismans: ShopTalisman[];
  die: ShopDie | null;
  rerolls: number;
}

export type RunPhase = 'hand' | 'shop' | 'over' | 'won';

export interface RunHand {
  faces: number[];
  held: boolean[];
  rollsUsed: number;
  forcedRerolls: number;
  /** Questa mano ha subito il lancio caotico del TILT pieno. */
  chaosHit: boolean;
}

export interface RunState {
  seed: string;
  character: CharacterId;
  phase: RunPhase;
  round: number;
  target: number;
  roundScore: number;
  handsLeft: number;
  pips: number;
  talismans: string[];
  kinds: DieKind[];
  /** Ultime facce risolte per dado (memoria del Piombato). 0 = mai lanciato. */
  lastFaces: number[];
  /** Combo incassate per ogni Dado di Vetro (a GLASS_USES si rompe). */
  glassUses: number[];
  tilt: number;
  /** TILT pieno: il prossimo lancio è caotico. */
  tiltPrimed: boolean;
  /** Boss dei round 3/6/9/12, permutati dal seed. */
  bossOrder: BossRule[];
  /** Pool rilanci del round (solo boss "pool-rilanci", altrimenti null). */
  roundRerollPool: number | null;
  hand: RunHand | null;
  shop: ShopOffer | null;
  /** Statistiche di fine run. */
  handsPlayed: number;
  bestHand: number;
}

export function currentBoss(state: RunState): BossRule | null {
  if (!isBossRound(state.round)) return null;
  return state.bossOrder[state.round / 3 - 1] ?? null;
}

function setupRound(state: RunState): void {
  const char = CHARACTERS[state.character];
  state.target = targetForRound(state.round, char.targetScale);
  state.roundScore = 0;
  state.handsLeft = HANDS_PER_ROUND;
  state.tilt = Math.max(state.tilt, char.roundStartTilt);
  state.roundRerollPool = currentBoss(state) === 'pool-rilanci' ? BOSS_POOL_REROLLS : null;
}

export function newRun(seed: string, character: CharacterId = 'bilanciato'): RunState {
  const char = CHARACTERS[character];
  // Stream rng separato per l'ordine dei boss: non consuma il flusso di gioco.
  const bossRng = rngFromString(`${seed}::boss`);
  const order = BOSS_RULES.slice();
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(bossRng() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  const kinds = Array.from({ length: DICE_COUNT }, () => 'standard' as DieKind);
  if (char.startDie) kinds[0] = char.startDie;
  const state: RunState = {
    seed,
    character,
    phase: 'hand',
    round: 1,
    target: 0,
    roundScore: 0,
    handsLeft: HANDS_PER_ROUND,
    pips: 4,
    talismans: [],
    kinds,
    lastFaces: Array.from({ length: DICE_COUNT }, () => 0),
    glassUses: Array.from({ length: DICE_COUNT }, () => 0),
    tilt: 0,
    tiltPrimed: false,
    bossOrder: order,
    roundRerollPool: null,
    hand: null,
    shop: null,
    handsPlayed: 0,
    bestHand: 0,
  };
  setupRound(state);
  return state;
}

function chaosFor(state: RunState): Chaos {
  if (!state.tiltPrimed) return 'none';
  return state.talismans.includes('risk-berserk') ? 'better' : 'worse';
}

/** Applica un lancio (iniziale o rilancio) gestendo il caos del TILT pieno. */
function applyRoll(state: RunState, rng: Rng, held: boolean[]): { faces: number[]; chaos: boolean } {
  const chaos = chaosFor(state);
  const faces = rollDiceSet({ kinds: state.kinds, prevFaces: state.lastFaces, held, rng, chaos });
  state.lastFaces = faces.slice();
  if (chaos !== 'none') {
    state.tilt = 0;
    state.tiltPrimed = false;
  }
  return { faces, chaos: chaos !== 'none' };
}

export function startHand(state: RunState, rng: Rng): void {
  if (state.phase !== 'hand' || state.hand) return;
  // Boss "Mano Tremante": ogni mano parte col TILT già carico.
  if (currentBoss(state) === 'tremante') {
    state.tilt = Math.min(TILT_MAX, state.tilt + TILT_PER_FORCE);
    if (state.tilt >= TILT_MAX) state.tiltPrimed = true;
  }
  const held = Array.from({ length: DICE_COUNT }, () => false);
  const { faces, chaos } = applyRoll(state, rng, held);
  state.hand = { faces, held, rollsUsed: 1, forcedRerolls: 0, chaosHit: chaos };
}

export function toggleHold(state: RunState, index: number): void {
  const hand = state.hand;
  if (!hand || index < 0 || index >= hand.held.length) return;
  hand.held[index] = !hand.held[index];
}

export function canRerollFree(state: RunState): boolean {
  const hand = state.hand;
  if (!hand || hand.rollsUsed >= MAX_ROLLS || !hand.held.some((h) => !h)) return false;
  if (state.roundRerollPool !== null && state.roundRerollPool <= 0) return false;
  return true;
}

/** Il rilancio forzato è la firma del gioco: sempre possibile, ma carica il TILT. */
export function canRerollForced(state: RunState): boolean {
  const hand = state.hand;
  if (!hand || !hand.held.some((h) => !h)) return false;
  if (hand.rollsUsed < MAX_ROLLS && canRerollFree(state)) return false;
  return true;
}

export function rerollFree(state: RunState, rng: Rng): void {
  const hand = state.hand;
  if (!hand || !canRerollFree(state)) return;
  if (state.roundRerollPool !== null) state.roundRerollPool -= 1;
  const { faces, chaos } = applyRoll(state, rng, hand.held);
  hand.faces = faces;
  hand.rollsUsed += 1;
  if (chaos) hand.chaosHit = true;
}

export function rerollForced(state: RunState, rng: Rng): void {
  const hand = state.hand;
  if (!hand || !canRerollForced(state)) return;
  hand.forcedRerolls += 1;
  state.tilt = Math.min(TILT_MAX, state.tilt + TILT_PER_FORCE);
  if (state.tilt >= TILT_MAX) state.tiltPrimed = true;
  const { faces, chaos } = applyRoll(state, rng, hand.held);
  hand.faces = faces;
  hand.rollsUsed += 1;
  if (chaos) hand.chaosHit = true;
}

export function scoreCtxOf(state: RunState): Parameters<typeof scoreHandCtx>[1] {
  const hand = state.hand!;
  return {
    talismans: state.talismans,
    kinds: state.kinds,
    forcedRerolls: hand.forcedRerolls,
    freeRerolls: hand.rollsUsed - 1 - hand.forcedRerolls,
    tilt: state.tilt,
    tiltChaos: hand.chaosHit,
    round: state.round,
    handsLeft: state.handsLeft,
    bossRule: currentBoss(state),
  };
}

export interface HandOutcome {
  result: ScoredHandFull;
  roundCleared: boolean;
  runOver: boolean;
  reward: number;
  /** Indici dei Dadi di Vetro andati in frantumi incassando questa mano. */
  shattered: number[];
}

export function closeHand(state: RunState, rng: Rng): HandOutcome | null {
  const hand = state.hand;
  if (!hand || state.phase !== 'hand') return null;
  const result = scoreHandCtx(hand.faces, scoreCtxOf(state));
  state.roundScore += result.score;
  state.handsLeft -= 1;
  state.handsPlayed += 1;
  state.bestHand = Math.max(state.bestHand, result.score);

  // Il Dado di Vetro conta le combo incassate: alla terza va in frantumi.
  const shattered: number[] = [];
  for (const i of result.best.usedIndices) {
    if (state.kinds[i] !== 'vetro') continue;
    state.glassUses[i] = (state.glassUses[i] ?? 0) + 1;
    if (state.glassUses[i]! >= GLASS_USES) {
      state.kinds[i] = 'standard';
      state.glassUses[i] = 0;
      shattered.push(i);
    }
  }
  state.hand = null;

  if (state.roundScore >= state.target) {
    const reward = roundReward(state.handsLeft, state.pips);
    state.pips += reward;
    state.phase = 'shop';
    state.shop = rollShop(state, rng);
    return { result, roundCleared: true, runOver: false, reward, shattered };
  }
  if (state.handsLeft <= 0) {
    state.phase = 'over';
    return { result, roundCleared: false, runOver: true, reward: 0, shattered };
  }
  return { result, roundCleared: false, runOver: false, reward: 0, shattered };
}

// ── Il banco ────────────────────────────────────────────────────────────────

const RARITY_ROLL: [TalismanRarity, number][] = [
  ['leggendario', 0.06],
  ['raro', 0.30],
  ['comune', 1],
];

function talismanPrice(state: RunState, id: string): number {
  const def = TALISMAN_BY_ID.get(id)!;
  const char = CHARACTERS[state.character];
  const discount = def.family === 'rischio' ? (char.riskDiscount ?? 0) : 0;
  return Math.max(1, def.price - discount);
}

function rollShop(state: RunState, rng: Rng): ShopOffer {
  const pool = TALISMANS.filter((t) => !state.talismans.includes(t.id));
  const picks: ShopTalisman[] = [];
  for (let i = 0; i < 2 && pool.length > 0; i++) {
    const r = rng();
    const rarity = RARITY_ROLL.find(([, p]) => r < p)![0];
    let candidates = pool.filter((t) => t.rarity === rarity && !picks.some((p) => p.id === t.id));
    if (candidates.length === 0) candidates = pool.filter((t) => !picks.some((p) => p.id === t.id));
    if (candidates.length === 0) break;
    const chosen = candidates[Math.floor(rng() * candidates.length)]!;
    picks.push({ id: chosen.id, price: talismanPrice(state, chosen.id), sold: false });
  }
  const char = CHARACTERS[state.character];
  const hasStandardSlot = state.kinds.includes('standard');
  let die: ShopDie | null = null;
  if (hasStandardSlot) {
    const kinds = Object.keys(DIE_PRICE) as (keyof typeof DIE_PRICE)[];
    const kind = kinds[Math.floor(rng() * kinds.length)]!;
    die = { kind, price: Math.max(1, DIE_PRICE[kind] - (char.dieDiscount ?? 0)), sold: false };
  }
  return { talismans: picks, die, rerolls: 0 };
}

export function talismanSlots(state: RunState): number {
  return CHARACTERS[state.character].slots;
}

export function buyTalisman(state: RunState, index: number): boolean {
  const offer = state.shop?.talismans[index];
  if (!offer || offer.sold) return false;
  if (state.talismans.length >= talismanSlots(state)) return false;
  if (state.pips < offer.price) return false;
  state.pips -= offer.price;
  state.talismans.push(offer.id);
  offer.sold = true;
  return true;
}

export function buyDie(state: RunState): boolean {
  const offer = state.shop?.die;
  if (!offer || offer.sold) return false;
  if (state.pips < offer.price) return false;
  const slot = state.kinds.indexOf('standard');
  if (slot === -1) return false;
  state.pips -= offer.price;
  state.kinds[slot] = offer.kind;
  state.lastFaces[slot] = 0;
  state.glassUses[slot] = 0;
  offer.sold = true;
  return true;
}

export function rerollShop(state: RunState, rng: Rng): boolean {
  if (state.phase !== 'shop' || !state.shop) return false;
  if (state.pips < SHOP_REROLL_COST) return false;
  state.pips -= SHOP_REROLL_COST;
  const rerolls = state.shop.rerolls + 1;
  state.shop = rollShop(state, rng);
  state.shop.rerolls = rerolls;
  return true;
}

export function leaveShop(state: RunState): void {
  if (state.phase !== 'shop') return;
  state.shop = null;
  state.round += 1;
  if (state.round > TOTAL_ROUNDS) {
    state.phase = 'won';
    return;
  }
  setupRound(state);
  state.phase = 'hand';
}

export function talismanDef(id: string) {
  return TALISMAN_BY_ID.get(id) ?? null;
}
